import {
  IServiceProvider,
  IServiceType,
} from 'src/services/api/ServiceRequestDashboard/apiContracts';

export type RoleKey =
  | 'Service Provider'
  | 'Service Manager'
  | 'Property Developer';

export type ContactRole = RoleKey | 'Unrecognized';

export const KNOWN_ROLES: readonly RoleKey[] = [
  'Service Provider',
  'Service Manager',
  'Property Developer',
];

export interface CanonicalContact {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  companyName: string | null;
  role: ContactRole;
  originalRole: string;
  servicesProvided: string[];
  profileImage: string | null;
}

const isKnownRole = (role: string): role is RoleKey => {
  return (KNOWN_ROLES as readonly string[]).includes(role);
};

const normalizeServicesProvided = (
  services: IServiceType[] | string[] | null | undefined
): string[] => {
  if (!services || services.length === 0) {
    return [];
  }

  const first = services[0];

  if (typeof first === 'string') {
    return services as string[];
  }

  return (services as IServiceType[])
    .map((service) => service.issueType)
    .filter((issueType): issueType is string => Boolean(issueType));
};

export const normalizeContact = (
  provider: IServiceProvider
): CanonicalContact => {
  const rawRole = provider.role ?? '';
  const role: ContactRole = isKnownRole(rawRole) ? rawRole : 'Unrecognized';

  const firstName = provider.firstname ?? '';
  const lastName = provider.lastname ?? '';

  return {
    id: provider.userId,
    firstName,
    lastName,
    fullName: `${firstName} ${lastName}`.trim(),
    companyName: provider.companyName ?? null,
    role,
    originalRole: rawRole,
    servicesProvided: normalizeServicesProvided(provider.servicesProvided),
    profileImage: provider.profileImage,
  };
};

export const normalizeContacts = (
  providers: IServiceProvider[] | null | undefined
): CanonicalContact[] => {
  if (!providers || providers.length === 0) {
    return [];
  }

  return providers.map(normalizeContact);
};
